import { useEffect, useMemo, useState } from 'react'
import Modal from 'react-modal'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { adminService } from '../../services/admin.service'
import { ScheduledBlastListItemDto, VenueSpecialDto } from '@ivy-monorepo/shared'
import DateTimePicker from '../ui/DateTimePicker'
import ToggleInput from '../ui/ToggleInput'

dayjs.extend(utc)
dayjs.extend(timezone)

interface BlastModalProps {
  isOpen: boolean
  onClose: () => void
  venuePublicId: string
  /** Edit this pending scheduled blast instead of sending a new one. */
  editBlast?: ScheduledBlastListItemDto | null
  onSaved?: (blast: ScheduledBlastListItemDto) => void
}

const metersPerMile = 1609.34

type DeepLinkTarget = 'none' | 'venuePeek' | 'eventsTab' | 'upcomingEvent'

const BlastModal = ({ isOpen, onClose, venuePublicId, editBlast, onSaved }: BlastModalProps) => {
  const [message, setMessage] = useState('')
  const [radius, setRadius] = useState<string>('')
  const [recipientCount, setRecipientCount] = useState<number | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [scheduledDate, setScheduledDate] = useState<string | null>(null)
  const [isScheduled, setIsScheduled] = useState<boolean>(false)
  const [deepLinkTarget, setDeepLinkTarget] = useState<DeepLinkTarget>('venuePeek')
  const [venueSpecials, setVenueSpecials] = useState<VenueSpecialDto[]>([])
  const [selectedSpecialPublicId, setSelectedSpecialPublicId] = useState<string>('')

  // Reset state when modal opens
  useEffect(() => {
    if (!isOpen) return
    setRecipientCount(null)
    if (editBlast) {
      setMessage(editBlast.message)
      setRadius(String(Math.round((editBlast.radius / metersPerMile) * 100) / 100))
      setDeepLinkTarget(editBlast.deepLinkTarget ?? 'none')
      setSelectedSpecialPublicId(editBlast.deepLinkVenueSpecialPublicId ?? '')
      setScheduledDate(editBlast.scheduledDate ?? null)
      setIsScheduled(true)
      return
    }
    setMessage('')
    setRadius('')
    setDeepLinkTarget('venuePeek')
    setSelectedSpecialPublicId('')
    setScheduledDate(null)
    setIsScheduled(false)
  }, [isOpen, editBlast])

  useEffect(() => {
    if (!isOpen) return

    if (isScheduled || !radius || Number(radius) <= 0) {
      setRecipientCount(null)
      return
    }

    const timeout = setTimeout(async () => {
      try {
        setIsCalculating(true)
        const count = await adminService.calculateUsersInVenueRadius(
          venuePublicId,
          Number(radius) * metersPerMile,
        )
        setRecipientCount(count)
      } catch (err) {
        console.error('Failed to fetch blast recipient count', err)
      } finally {
        setIsCalculating(false)
      }
    }, 500)

    return () => clearTimeout(timeout)
  }, [radius, venuePublicId, message, isOpen, deepLinkTarget, selectedSpecialPublicId, isScheduled])

  useEffect(() => {
    if (!isOpen) return

    const fetchSpecials = async () => {
      try {
        const data = await adminService.getVenueSpecials(venuePublicId)
        setVenueSpecials(data)
      } catch (err) {
        console.error('Failed to fetch venue specials for blast deep link', err)
        setVenueSpecials([])
      }
    }

    fetchSpecials()
  }, [isOpen, venuePublicId])

  const upcomingEventOptions = useMemo(() => {
    const now = new Date()
    return venueSpecials
      .filter(special => {
        if (!special.eventEndDate) {
          return false
        }
        const endDate = new Date(special.eventEndDate)
        if (Number.isNaN(endDate.getTime()) || endDate < now) {
          return false
        }

        // Must also be currently visible to customers (matches the mobile
        // app's feed filters — an event outside its spotlight window can't
        // be resolved by the app's deep-link handler either).
        if (special.startDate) {
          const spotlightStart = new Date(special.startDate)
          if (!Number.isNaN(spotlightStart.getTime()) && spotlightStart > now) {
            return false
          }
        }
        if (special.endDate) {
          const spotlightEnd = new Date(special.endDate)
          if (!Number.isNaN(spotlightEnd.getTime()) && spotlightEnd < now) {
            return false
          }
        }

        return true
      })
      .sort((a, b) => {
        const aStart = a.eventStartDate ? new Date(a.eventStartDate).getTime() : Number.POSITIVE_INFINITY
        const bStart = b.eventStartDate ? new Date(b.eventStartDate).getTime() : Number.POSITIVE_INFINITY
        if (aStart !== bStart) {
          return aStart - bStart
        }
        const aEnd = a.eventEndDate ? new Date(a.eventEndDate).getTime() : Number.POSITIVE_INFINITY
        const bEnd = b.eventEndDate ? new Date(b.eventEndDate).getTime() : Number.POSITIVE_INFINITY
        return aEnd - bEnd
      })
  }, [venueSpecials])

  // The blast's event may have left the spotlight window since it was scheduled;
  // keep it selectable so editing doesn't silently drop the link.
  const editEventMissing =
    !!editBlast?.deepLinkVenueSpecialPublicId &&
    !upcomingEventOptions.some(special => special.publicId === editBlast.deepLinkVenueSpecialPublicId)
  const editEventName = venueSpecials.find(
    special => special.publicId === editBlast?.deepLinkVenueSpecialPublicId,
  )?.name

  const deepLinkSelectValue =
    deepLinkTarget === 'upcomingEvent' && selectedSpecialPublicId ? `event:${selectedSpecialPublicId}` : deepLinkTarget

  const handleSave = async () => {
    if (!editBlast || !scheduledDate) return
    try {
      setIsSending(true)
      const saved = await adminService.updateScheduledBlast(editBlast.publicId, {
        message,
        radius: Number(radius) * metersPerMile,
        scheduledDate,
        deepLinkTarget,
        deepLinkVenueSpecialPublicId: deepLinkTarget === 'upcomingEvent' ? selectedSpecialPublicId || null : null,
      })
      onSaved?.(saved)
      onClose()
    } catch (err: any) {
      console.error('Failed to update scheduled blast', err)
      alert(err?.response?.data?.friendlyMessage ?? err?.response?.data?.message ?? 'Failed to save blast. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  const handleSend = async () => {
    if (!message || !radius) {
      alert('Please enter a message and radius.')
      return
    }
    if (editBlast) {
      await handleSave()
      return
    }

    if (!window.confirm('Are you sure you want to send this blast?')) {
      return
    }

    try {
      setIsSending(true)

      const date = isScheduled ? scheduledDate : null

      await adminService.blastVenue(
        venuePublicId,
        message,
        Number(radius) * metersPerMile,
        false,
        deepLinkTarget !== 'none',
        deepLinkTarget,
        deepLinkTarget === 'upcomingEvent' ? selectedSpecialPublicId || null : null,
        date
      )
      onClose()
    } catch (err) {
      console.error('Failed to ', err)
      alert('Failed to send blast. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className="modal-popup blast-popup"
      overlayClassName="modal-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onAfterClose={() => {
        document.body.classList.remove('ReactModal__Body--open')
      }}
    >
      <div className="blast-modal-content modal-content-wrapper">
        <h2>{editBlast ? 'Edit scheduled blast' : 'Send blast'}</h2>
        <form className='modal-body-content'>
          <div className="form-group">
            <label>Message</label>
            <textarea rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Enter message to send" />
          </div>
          <div className="form-group">
            <label>Radius</label>
            <input
              type="number"
              value={radius}
              min={0}
              max={50}
              onChange={e => {
                const value = Number(e.target.value)

                if (Number.isNaN(value)) {
                  setRadius("")
                  return
                }

                setRadius(String(Math.min(value, 50)))
              }}
              placeholder="Enter radius in miles"
            />
          </div>
          <div className="form-group row">
            <label>Schedule Blast (Central Time)</label>
            <div className="date-group">
              {!editBlast && (
                <ToggleInput
                  value={isScheduled}
                  onChange={() => setIsScheduled(!isScheduled)}
                />
              )}
              <DateTimePicker
                value={scheduledDate}
                onChange={(val) => setScheduledDate(val)}
                disabled={!isScheduled}
                min={dayjs().tz('America/Chicago').startOf('d').toISOString()}
                max={dayjs().tz('America/Chicago').add(7, 'day').endOf('d').toISOString()}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Deep link to</label>
            <select
              value={deepLinkSelectValue}
              onChange={e => {
                const value = e.target.value
                if (value.startsWith('event:')) {
                  setDeepLinkTarget('upcomingEvent')
                  setSelectedSpecialPublicId(value.replace('event:', ''))
                } else {
                  setDeepLinkTarget(value as DeepLinkTarget)
                  setSelectedSpecialPublicId('')
                }
              }}
            >
              <option value="none">Nothing</option>
              <option value="venuePeek">Venue Peek</option>
              <option value="eventsTab">Events Tab</option>
              {editEventMissing && (
                <option value={`event:${editBlast!.deepLinkVenueSpecialPublicId}`}>
                  {editEventName ?? 'Current event'}
                </option>
              )}
              {upcomingEventOptions.map(special => (
                <option key={special.publicId} value={`event:${special.publicId}`}>
                  {special.name}
                  {special.eventStartDate ? ` (${new Date(special.eventStartDate).toLocaleDateString()})` : ''}
                </option>
              ))}
            </select>
          </div>
        </form>
        <div className="modal-footer-content">
          {(recipientCount !== null && !isScheduled) && (
            <p className="recipient-count">{isCalculating ? 'Calculating…' : `${recipientCount} user${recipientCount === 1 ? '' : 's'} in radius`}</p>
          )}
          <button className="styled-button" onClick={handleSend} disabled={isSending || !message || !radius || (isScheduled && !scheduledDate)}>
            {editBlast ? (isSending ? 'Saving…' : 'Save') : isSending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default BlastModal
